import React, { useCallback } from "react";
import { connect } from "react-redux";

import Pagination from "antd/es/pagination";
import { useIntl } from "react-intl";

import { EntityTypeSupportingDataMerging } from "@mapmycustomers/shared/types/entity";
import { DuplicatePair } from "@mapmycustomers/shared/types/entity/Duplicate";

import ButtonLink from "@app/component/ButtonLink";
import Table from "@app/component/Table";
import { MERGE_DUPLICATES_TABLE_PAGE_SIZE } from "@app/scene/settings/util/const";
import { RootState } from "@app/store/rootReducer";

import {
  getDuplicates,
  getDuplicatesLoading,
  getDuplicatesTotal,
  getEntityType,
  getPage,
  getSelectedPair,
  isDismissedShowed,
} from "../../store";
import {
  changeDuplicatePairDismission,
  fetchDuplicates,
  hideDismissed,
  selectDuplicatePair,
} from "../../store/actions";

import PreviewMergePane from "./component/PreviewMergePane";
import styles from "./EntityTable.module.scss";
import useDuplicateColumns from "./util/useDuplicateColumns";

interface Props {
  changeDuplicatePairDismission: typeof changeDuplicatePairDismission.request;
  duplicates: DuplicatePair[];
  entityType: EntityTypeSupportingDataMerging;
  hideDismissed: typeof hideDismissed;
  isDismissedShowed: boolean;
  loading: boolean;
  onFetchDuplicates: typeof fetchDuplicates.request;
  page: number;
  selectDuplicatePair: typeof selectDuplicatePair.request;
  showPreviewMergePane: boolean;
  total: number;
}

const EntityTable: React.FC<Props> = ({
  changeDuplicatePairDismission,
  duplicates,
  entityType,
  hideDismissed,
  isDismissedShowed,
  loading,
  onFetchDuplicates,
  page,
  selectDuplicatePair,
  showPreviewMergePane,
  total,
}) => {
  const intl = useIntl();

  const handlePageChange = useCallback(
    (page: number) => onFetchDuplicates({ page: page - 1 }),
    [onFetchDuplicates]
  );

  const columns = useDuplicateColumns(
    intl,
    entityType,
    changeDuplicatePairDismission,
    selectDuplicatePair
  );

  return (
    <div className={styles.container}>
      <div className={styles.tableWrapper}>
        {isDismissedShowed && (
          <div className={styles.dismissedDescription}>
            {intl.formatMessage(
              {
                id: "settings.mergeDuplicates.table.dismissedDescription",
                defaultMessage:
                  "Showing dismissed potential duplicates only. <a>Return to standard view?</a>",
                description: "Showing dismissed potential duplicates only.",
              },
              {
                a: (text) => <ButtonLink onClick={() => hideDismissed()}>{text}</ButtonLink>,
              }
            )}
          </div>
        )}
        <Table<DuplicatePair>
          columns={columns}
          dataSource={duplicates}
          loading={loading}
          noHover
          pagination={false}
          rowKey="id"
          showBorder
        />
        <Pagination
          className={styles.pagination}
          current={page + 1}
          defaultPageSize={MERGE_DUPLICATES_TABLE_PAGE_SIZE}
          onChange={handlePageChange}
          showSizeChanger={false}
          total={total}
        />
      </div>
      {showPreviewMergePane && (
        <PreviewMergePane entityType={entityType} onHide={() => selectDuplicatePair(undefined)} />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  duplicates: getDuplicates(state),
  entityType: getEntityType(state),
  isDismissedShowed: isDismissedShowed(state),
  loading: getDuplicatesLoading(state),
  page: getPage(state),
  showPreviewMergePane: !!getSelectedPair(state),
  total: getDuplicatesTotal(state),
});

const mapDispatchToProps = {
  changeDuplicatePairDismission: changeDuplicatePairDismission.request,
  hideDismissed,
  onFetchDuplicates: fetchDuplicates.request,
  selectDuplicatePair: selectDuplicatePair.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EntityTable);
